import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  Globe, TrendUp, TrendDown, Users, Buildings, Handshake, MapPin,
  GenderIntersex, Briefcase, FilePdf, FloppyDisk, Trash, Eye, Clock,
  ArrowUp, ArrowDown, ChartBar, CaretUp, CaretDown, Minus, NotePencil, TreeStructure} from '@phosphor-icons/react';
import { getOrgImpactDashboard, saveOrgImpactReport, listOrgImpactReports, getOrgImpactReport, deleteOrgImpactReport } from '../services/api';
import apiInstance from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHero } from '../components/motion/PageHero';

const ODD_ICONS = {
  odd1: Users,
  odd5: GenderIntersex,
  odd8: Briefcase,
  odd10: TrendUp,
  odd11: MapPin,
  odd17: Handshake,
};

const TYPE_LABELS = {
  accompagnement: 'Accompagnement',
  creation: 'Creation',
  transformation: 'Transformation',
  adhesion: 'Adhesion',
  formation: 'Formation',
  porteur_projet: 'Porteur de projet',
  beneficiaire: 'Beneficiaire',
  partenaire: 'Partenaire',
  membre: 'Membre',
  non_defini: 'Non defini',
};

const PIE_COLORS = ['#2D5A4C', '#D47A62', '#8DB0A1', '#5E6D66', '#00AEEF', '#80C51F', '#EA3C3A', '#FF8E00'];

export default function OrgImpactPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [savedReports, setSavedReports] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewReport, setViewReport] = useState(null);
  const [reportTitle, setReportTitle] = useState('');
  const [reportNotes, setReportNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [period]);

  useEffect(() => {
    fetchSavedReports();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getOrgImpactDashboard(period);
      setData(res.data);
    } catch (err) {
      console.error('Error fetching org impact:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedReports = async () => {
    try {
      const res = await listOrgImpactReports();
      setSavedReports(res.data.items || []);
    } catch { /* ignore */ }
  };

  const handleSaveReport = async () => {
    if (!reportTitle.trim()) { toast.error('Titre requis'); return; }
    setSaving(true);
    try {
      await saveOrgImpactReport({ title: reportTitle, period, notes: reportNotes });
      toast.success('Rapport sauvegarde !');
      setSaveDialogOpen(false);
      setReportTitle('');
      setReportNotes('');
      fetchSavedReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleViewReport = async (id) => {
    try {
      const res = await getOrgImpactReport(id);
      setViewReport(res.data);
      setViewDialogOpen(true);
    } catch {
      toast.error('Erreur chargement rapport');
    }
  };

  const handleDeleteReport = async (id) => {
    try {
      await deleteOrgImpactReport(id);
      toast.success('Rapport supprime');
      fetchSavedReports();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleExportPDF = async (reportId) => {
    try {
      toast.info('Generation du PDF...');
      const res = await apiInstance.get(`/impact-ess/org-report/${reportId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_impact_org_${reportId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF telecharge !');
    } catch {
      toast.error('Erreur PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  if (!data) return null;

  const { odd_indicators, summary, monthly_evolution, territory_breakdown, global_averages, dossier_types, contact_types } = data;
  const oddKeys = ['odd1', 'odd5', 'odd8', 'odd10', 'odd11', 'odd17'];

  const radarData = oddKeys.map(key => ({
    odd: `ODD ${odd_indicators[key].number}`,
    progress: Math.min(odd_indicators[key].progress, 100),
  }));

  const ComparisonBadge = ({ value, average, suffix = '' }) => {
    if (!average || average === 0) return null;
    const diff = value - average;
    const pct = Math.round((diff / average) * 100);
    if (pct === 0) return <span className="inline-flex items-center text-[10px] text-[#5E6D66]"><Minus size={10} className="mr-0.5" />Moyenne</span>;
    return pct > 0
      ? <span className="inline-flex items-center text-[10px] text-green-600"><CaretUp size={10} weight="fill" />+{pct}% vs moy.</span>
      : <span className="inline-flex items-center text-[10px] text-red-500"><CaretDown size={10} weight="fill" />{pct}% vs moy.</span>;
  };

  const ProgressGauge = ({ value, max, color, size = 64 }) => {
    const pct = Math.min((value / Math.max(max, 1)) * 100, 100);
    const radius = (size - 8) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (pct / 100) * circumference;
    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#E8E6E1" strokeWidth="4" />
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="4"
            strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
            className="transition-all duration-1000" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-bold text-[#1A2421]">{Math.round(pct)}%</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="org-impact-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Impact par organisation"
        subtitle="Bilan d'impact ESS structure par structure"
        icon={<TreeStructure size={24} weight="duotone" className="text-white" />}
        gradient="from-[#80C51F] to-[#2D5A4C]"
        accents={['#00AEEF', '#D5007D']}
      />

      {/* Summary KPIs with comparison */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="org-summary-kpis">
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <Buildings size={18} className="text-[#2D5A4C]" />
              <ComparisonBadge value={summary.total_organizations} average={global_averages?.avg_organizations} />
            </div>
            <p className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>{summary.total_organizations}</p>
            <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider font-bold mt-0.5">Structures ESS</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <Users size={18} className="text-[#D47A62]" />
              <ComparisonBadge value={summary.total_contacts} average={global_averages?.avg_contacts} />
            </div>
            <p className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>{summary.total_contacts}</p>
            <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider font-bold mt-0.5">Contacts</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <ChartBar size={18} className="text-[#FF8E00]" />
              <ComparisonBadge value={summary.total_dossiers} average={global_averages?.avg_dossiers} />
            </div>
            <p className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>{summary.total_dossiers}</p>
            <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider font-bold mt-0.5">Dossiers</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <Handshake size={18} className="text-[#2D5A4C]" />
              <ComparisonBadge value={summary.total_partners} average={global_averages?.avg_partners} />
            </div>
            <p className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>{summary.total_partners}</p>
            <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider font-bold mt-0.5">Partenaires</p>
          </CardContent>
        </Card>
      </div>

      {/* ODD Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="org-odd-cards">
        {oddKeys.map(key => {
          const ind = odd_indicators[key];
          const Icon = ODD_ICONS[key];
          return (
            <Card key={key} className="border-[#E8E6E1] shadow-sm overflow-hidden hover:-translate-y-0.5 transition-transform duration-200" data-testid={`org-odd-${key}`}>
              <div className="h-1.5" style={{ background: ind.color }} />
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge style={{ background: ind.color, color: 'white' }} className="text-[10px] font-bold px-2">
                        ODD {ind.number}
                      </Badge>
                      <span className="text-xs font-semibold text-[#1A2421] truncate">{ind.label}</span>
                    </div>
                    <div className="text-xl font-bold text-[#1A2421] mt-2" style={{ fontFamily: 'Manrope' }}>
                      {key === 'odd5' || key === 'odd10' ? `${ind.value}%` : ind.value}
                    </div>
                    <p className="text-[10px] text-[#5E6D66] mt-0.5">
                      Objectif: {key === 'odd5' || key === 'odd10' ? `${ind.objective}%` : ind.objective}
                    </p>
                  </div>
                  <ProgressGauge value={ind.value} max={ind.objective} color={ind.color} />
                </div>
                <div className="mt-3 pt-2 border-t border-[#F0EFEB] space-y-0.5">
                  {key === 'odd1' && (
                    <>
                      <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Beneficiaires</span><span className="font-semibold">{ind.details.beneficiaires}</span></div>
                      <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Dossiers valides</span><span className="font-semibold">{ind.details.dossiers_valides}</span></div>
                    </>
                  )}
                  {key === 'odd5' && (
                    <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">F: {ind.details.femmes} | H: {ind.details.hommes}</span><span className="font-semibold">{ind.details.ratio_femmes}%</span></div>
                  )}
                  {key === 'odd8' && (
                    <>
                      <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Cooperatives</span><span className="font-semibold">{ind.details.cooperatives}</span></div>
                      <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Collecte</span><span className="font-semibold">{(ind.details.montant_collecte || 0).toLocaleString('fr-FR')} EUR</span></div>
                    </>
                  )}
                  {key === 'odd10' && (
                    <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Territoires</span><span className="font-semibold">{ind.details.territoires_couverts}/{ind.details.total_territoires}</span></div>
                  )}
                  {key === 'odd17' && (
                    <div className="flex justify-between text-[10px]"><span className="text-[#5E6D66]">Partenaires actifs</span><span className="font-semibold">{ind.details.partenaires_actifs}</span></div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Radar */}
        <Card className="border-[#E8E6E1] shadow-sm" data-testid="org-radar-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
              <Globe size={16} className="text-[#2D5A4C]" />
              Progression ODD
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#E8E6E1" />
                  <PolarAngleAxis dataKey="odd" tick={{ fontSize: 10, fill: '#5E6D66' }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9, fill: '#5E6D66' }} />
                  <Radar name="Progression" dataKey="progress" stroke="#2D5A4C" fill="#2D5A4C" fillOpacity={0.25} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Territory */}
        <Card className="border-[#E8E6E1] shadow-sm" data-testid="org-territory-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
              <MapPin size={16} className="text-[#2D5A4C]" />
              Repartition territoriale
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={territory_breakdown} margin={{ top: 10, right: 0, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                  <XAxis dataKey="territoire" tick={{ fontSize: 9, fill: '#5E6D66' }}
                    tickFormatter={(v) => v.length > 8 ? v.slice(0, 8) + '.' : v} />
                  <YAxis tick={{ fontSize: 10, fill: '#5E6D66' }} />
                  <Tooltip contentStyle={{ background: 'white', border: '1px solid #E8E6E1', borderRadius: '8px', fontSize: 11 }} />
                  <Bar dataKey="organizations" name="Organisations" fill="#2D5A4C" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="contacts" name="Contacts" fill="#D47A62" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="dossiers" name="Dossiers" fill="#8DB0A1" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Dossier types */}
        {dossier_types && dossier_types.length > 0 && (
          <Card className="border-[#E8E6E1] shadow-sm" data-testid="dossier-types-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#1A2421]">Types de dossiers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-1/2 h-[180px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={dossier_types} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={70} innerRadius={35}>
                        {dossier_types.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => [v, 'Dossiers']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-1/2 space-y-1.5">
                  {dossier_types.slice(0, 6).map((d, i) => (
                    <div key={d.type} className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-xs text-[#5E6D66] truncate">{TYPE_LABELS[d.type] || d.type}</span>
                      <span className="text-xs font-bold text-[#1A2421] ml-auto">{d.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact types */}
        {contact_types && contact_types.length > 0 && (
          <Card className="border-[#E8E6E1] shadow-sm" data-testid="contact-types-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#1A2421]">Types de contacts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-1/2 h-[180px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={contact_types} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={70} innerRadius={35}>
                        {contact_types.map((_, i) => <Cell key={i} fill={PIE_COLORS[(i + 3) % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => [v, 'Contacts']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-1/2 space-y-1.5">
                  {contact_types.slice(0, 6).map((c, i) => (
                    <div key={c.type} className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: PIE_COLORS[(i + 3) % PIE_COLORS.length] }} />
                      <span className="text-xs text-[#5E6D66] truncate">{TYPE_LABELS[c.type] || c.type}</span>
                      <span className="text-xs font-bold text-[#1A2421] ml-auto">{c.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Monthly Evolution */}
      <Card className="border-[#E8E6E1] shadow-sm" data-testid="org-monthly-evolution">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
            <TrendUp size={16} className="text-[#2D5A4C]" />
            Evolution mensuelle
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthly_evolution} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                <XAxis dataKey="month_short" tick={{ fontSize: 10, fill: '#5E6D66' }} />
                <YAxis tick={{ fontSize: 10, fill: '#5E6D66' }} />
                <Tooltip contentStyle={{ background: 'white', border: '1px solid #E8E6E1', borderRadius: '8px', fontSize: 11 }} />
                <Area type="monotone" dataKey="organizations" name="Organisations" stroke="#2D5A4C" fill="#2D5A4C" fillOpacity={0.15} strokeWidth={2} />
                <Area type="monotone" dataKey="contacts" name="Contacts" stroke="#D47A62" fill="#D47A62" fillOpacity={0.1} strokeWidth={2} />
                <Area type="monotone" dataKey="dossiers" name="Dossiers" stroke="#8DB0A1" fill="#8DB0A1" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Saved Reports */}
      {savedReports.length > 0 && (
        <Card className="border-[#E8E6E1] shadow-sm" data-testid="saved-reports-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
              <Clock size={16} className="text-[#2D5A4C]" />
              Rapports sauvegardes ({savedReports.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#F0EFEB]">
              {savedReports.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-5 py-3 hover:bg-[#FAFAF8] transition-colors" data-testid={`saved-report-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A2421] truncate">{r.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-[#5E6D66]">{new Date(r.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      <Badge className="text-[9px] bg-[#F0EFEB] text-[#5E6D66]">{r.period === 'all' ? 'Toutes periodes' : r.period === 'month' ? '30j' : r.period === 'quarter' ? '3 mois' : '12 mois'}</Badge>
                      {r.created_by_name && <span className="text-[10px] text-[#8DB0A1]">par {r.created_by_name}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleViewReport(r.id)} data-testid={`view-report-${r.id}`}>
                      <Eye size={14} className="text-[#5E6D66]" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleExportPDF(r.id)} data-testid={`pdf-report-${r.id}`}>
                      <FilePdf size={14} className="text-[#2D5A4C]" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteReport(r.id)} data-testid={`delete-report-${r.id}`}>
                      <Trash size={14} className="text-red-400" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Save Report Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="save-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <FloppyDisk size={20} className="text-[#2D5A4C]" />
              Sauvegarder le rapport
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5E6D66]">
              Sauvegardez un instantane des indicateurs actuels pour reference future.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#5E6D66] mb-1 block">Titre</label>
              <Input
                value={reportTitle}
                onChange={(e) => setReportTitle(e.target.value)}
                placeholder="Ex: Rapport trimestriel Q1 2026"
                className="h-9 text-sm"
                data-testid="report-title-input"
              />
            </div>
            <div>
              <label className="text-xs text-[#5E6D66] mb-1 block">Notes (optionnel)</label>
              <Textarea
                value={reportNotes}
                onChange={(e) => setReportNotes(e.target.value)}
                placeholder="Observations, commentaires..."
                className="text-sm h-20 resize-none"
                data-testid="report-notes-input"
              />
            </div>
            <div className="flex items-center gap-2 text-xs text-[#5E6D66]">
              <Badge className="bg-[#F0EFEB] text-[#5E6D66]">{period === 'all' ? 'Toutes periodes' : period === 'month' ? '30 jours' : period === 'quarter' ? '3 mois' : '12 mois'}</Badge>
              <span>Donnees au {new Date().toLocaleDateString('fr-FR')}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveReport} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="confirm-save-report-btn">
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FloppyDisk size={14} className="mr-1" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Saved Report Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="view-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Eye size={20} className="text-[#2D5A4C]" />
              {viewReport?.title || 'Rapport'}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5E6D66]">
              Snapshot du {viewReport ? new Date(viewReport.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}
            </DialogDescription>
          </DialogHeader>
          {viewReport?.snapshot && (
            <div className="space-y-4">
              {viewReport.notes && (
                <div className="bg-[#F9F8F5] rounded-lg p-3 text-sm text-[#5E6D66] flex items-start gap-2">
                  <NotePencil size={16} className="flex-shrink-0 mt-0.5 text-[#2D5A4C]" />
                  {viewReport.notes}
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-[#F9F8F5] rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-[#1A2421]">{viewReport.snapshot.summary.total_organizations}</p>
                  <p className="text-[10px] text-[#5E6D66]">Organisations</p>
                </div>
                <div className="bg-[#F9F8F5] rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-[#1A2421]">{viewReport.snapshot.summary.total_contacts}</p>
                  <p className="text-[10px] text-[#5E6D66]">Contacts</p>
                </div>
                <div className="bg-[#F9F8F5] rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-[#1A2421]">{viewReport.snapshot.summary.total_dossiers}</p>
                  <p className="text-[10px] text-[#5E6D66]">Dossiers</p>
                </div>
                <div className="bg-[#F9F8F5] rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-[#1A2421]">{viewReport.snapshot.summary.total_partners}</p>
                  <p className="text-[10px] text-[#5E6D66]">Partenaires</p>
                </div>
              </div>
              <div className="space-y-2">
                {oddKeys.map(key => {
                  const ind = viewReport.snapshot.odd_indicators[key];
                  if (!ind) return null;
                  return (
                    <div key={key} className="flex items-center gap-3 py-1.5">
                      <Badge style={{ background: ind.color, color: 'white' }} className="text-[10px] w-14 justify-center flex-shrink-0">ODD {ind.number}</Badge>
                      <span className="text-xs text-[#1A2421] flex-1">{ind.label}</span>
                      <span className="text-xs font-bold text-[#1A2421]">{key === 'odd5' || key === 'odd10' ? `${ind.value}%` : ind.value}</span>
                      <span className="text-[10px] text-[#5E6D66]">/ {key === 'odd5' || key === 'odd10' ? `${ind.objective}%` : ind.objective}</span>
                      <Badge className={`text-[9px] ${ind.progress >= 100 ? 'bg-green-100 text-green-800' : ind.progress >= 50 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                        {Math.min(ind.progress, 100).toFixed(0)}%
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter>
            {viewReport && (
              <Button variant="outline" size="sm" onClick={() => handleExportPDF(viewReport.id)}>
                <FilePdf size={14} className="mr-1" />
                Exporter PDF
              </Button>
            )}
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
